import * as R from 'ramda';
import { put, call, fork, delay, select, takeEvery, takeLatest } from 'redux-saga/effects';
// common
import { setGoogleApiKey, sendLogOutRequest, initialDataLoadRequest } from '../../common/actions';
// components
import { openLoader, closeLoader } from '../../components/loader/actions';
// features
import { messageType } from '../sockets/constants';
import { setAuthoritiesRequest } from '../permission/actions';
import { receivedSwitchBranchSuccess } from '../branch/actions';
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
import { socketPostMessage, socketConnectRequest } from '../sockets/actions';
import {
  getCarrierPortalInfoSuccess,
  getChatMessageCarrierRequest,
  getCarrierResponseTokenSuccess,
} from '../carrier-portal/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// utilities
import routesMap from '../../utilities/routes';
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature auth
import * as A from './actions';
import { makeSelectAuthData } from './selectors';
import { createAzureSsoRedirectUrl, createAzureSsoCallbackRedirectUrl } from './azure-sso-urls';
//////////////////////////////////////////////////

const mobileWebUrl = 'https://ongoamous.com';

const auth = {
  password: 'secret',
  username: 'frontend',
};

function* handleDispatchAnyAction({ type }) {
  try {
    const { loggedIn, isDispatchedActions } = yield select(makeSelectAuthData());

    const condition = G.isAllTrue(
      loggedIn,
      R.not(G.stringContains(type, 'refreshTokenRequest')),
      R.not(G.stringContains(type, 'checkTokenRequest')),
    );

    const isDispatched = R.and(R.not(G.stringContains(type, 'setDispatchAction')), R.not(isDispatchedActions));

    if (R.and(condition, isDispatched)) {
      yield put(A.setDispatchAction(true));
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleDispatchAnyAction exception');
  }
}

function* logoutWithReloadSaga() {
  try {
    yield put(A.receivedLogOutSuccess());

    yield call(G.removeCurrentBranchGuid);
    yield call(G.removeRefreshTokenFromSession);
    yield call(G.removeAuthTokenFromSession);

    yield call(G.goToRoute, routesMap.loginPage);

    yield call(G.windowLocationReload);
  } catch (error) {
    yield call(G.windowLocationReload);
  }
}

function* handleCheckTokenSaga({ payload }) {
  try {
    const data = { token: payload };

    if (G.isNilOrEmpty(payload)) {
      yield call(logoutWithReloadSaga);

      return;
    }

    const { status } = yield call(sendRequest, 'post', endpointsMap.checkToken, { data, auth }, true);

    if (R.gte(status, 400)) {
      yield call(logoutWithReloadSaga);
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleCheckTokenSaga exception');
  }
}

function* handleRefreshTokenSaga() {
  try {
    const refreshToken = G.getRefreshTokenFromSession();

    const reqData = { grant_type: 'refresh_token', refresh_token: refreshToken };

    const res = yield call(sendRequest, 'post', endpointsMap.login, { data: reqData, auth }, true);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.setAuthTokenToSession, data.access_token);
      yield call(G.setRefreshTokenToSession, data.refresh_token);
      yield call(G.setAuthTokenExpirationToSession, data.expires_in);

      yield put(A.setDispatchAction(false));
      yield put(A.refreshTokenSuccess());
      // TODO: chech why we don't update tokens in sessionData makeSelectCurrentUserData if we need it
      // Check auth flow
    } else {
      yield call(logoutWithReloadSaga);
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleRefreshTokenSaga, exception');

    yield put(A.refreshTokenCatch());
  }
}

function* checkTokenAndRefreshTokenSaga() {
  try {
    const { loggedIn, isDispatchedActions } = yield select(makeSelectAuthData());

    if (loggedIn) {
      yield put(A.checkTokenRequest(G.getAuthTokenFromSession()));

      if (isDispatchedActions) {
        yield put(A.refreshTokenRequest());
      }
    }
  } catch (error) {
    yield call(G.handleException, error, 'checkTokenAndRefreshTokenSaga exception');
  }
}

function* checkTokenAndRefreshTokenSetDelaySaga() {
  while (true) { // eslint-disable-line
    yield delay(300000);

    yield call(checkTokenAndRefreshTokenSaga);
  }
}

function* handleLoginRequest({ payload }) {
  try {
    yield put(initialDataLoadRequest());

    const { password, username, callback } = payload;

    const reqData = { username, password, grant_type: 'password' };

    const res = yield call(sendRequest, 'post', endpointsMap.login, { data: reqData, auth }, true);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      if (R.isEmpty(data.authorities)) {
        yield call(G.showToastrMessage, 'info', 'messages:permissions:missing');

        yield call(G.goToRoute, routesMap.loginPage);
      } else {
        if (R.equals(data.user_type, GC.USER_TYPE_DRIVER)) {
          yield call(G.showToastrMessage, 'info', 'messages:user:driver:wrong-site');

          yield delay(2000);

          window.location.href = mobileWebUrl;

          return;
        }

        const branchGuid = data[GC.FIELD_BRANCH_UNDERSCORE_GUID];

        yield call(G.setCurrentBranchGuid, branchGuid);

        yield call(G.setAuthTokenToSession, data.access_token);
        yield call(G.setRefreshTokenToSession, data.refresh_token);
        yield call(G.setAuthTokenExpirationToSession, data.expires_in);

        yield put(A.receivedLogInSuccess(data));
        yield put(setAuthoritiesRequest(data.authorities));
      }

      G.setItemToWindow('amousCurrentUser', data);
    } else {
      yield call(G.handleFailResponse, res, 'handleLoginRequest fail', true);
    }

    G.callFunction(callback);
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:401');
    yield call(G.handleException, error, 'handleLoginRequest exception');

    yield call(logoutWithReloadSaga);
  }
}

const omitSessionPages = [
  GC.ROUTE_PATH_NAL_LOGIN,
  GC.ROUTE_PATH_NEW_PASSWORD,
  GC.ROUTE_PATH_OAUTH2_POPUP,
  GC.ROUTE_PATH_PRIVACY_POLICY,
  GC.ROUTE_PATH_RESET_PASSWORD,
  GC.ROUTE_PATH_AZURE_SSO_REDIRECT,
  GC.ROUTE_PATH_AZURE_SSO_CALLBACK,
  GC.ROUTE_PATH_BOSH_LOS_REDIRECT,
  GC.ROUTE_PATH_BOSH_LOS_CALLBACK,
  GC.ROUTE_PATH_NEW_PASSWORD_MOBILE,
  GC.ROUTE_PATH_TERMS_AND_CONDITIONS,
  GC.ROUTE_PATH_NORTHERN_DRIVER_PRIVACY_NOTICE,
  GC.ROUTE_PATH_NEW_PASSWORD_MOBILE_SUCCESS_PAGE,
  GC.ROUTE_PATH_NORTHERN_CARRIER_TRACKING_PRIVACY_NOTICE,
];

const clearWindowHash = () => {
  const cleanUrl = `${window.location.pathname}${window.location.search}`;

  window.history.replaceState({}, document.title, cleanUrl);
};

const replaceWindowLocation = (url) => window.location.replace(url);

const dispatchPopStateEvent = () => {
  const popStateEvent = typeof window.PopStateEvent === 'function'
    ? new window.PopStateEvent('popstate', { state: window.history.state })
    : new window.Event('popstate');

  window.dispatchEvent(popStateEvent);
};

const replaceWindowHistoryRoute = (url) => {
  window.history.replaceState({}, document.title, url);
  dispatchPopStateEvent();
};

const createLoginRouteWithError = (errorCode) => {
  const errorHash = new window.URLSearchParams({
    [GC.FIELD_ERROR]: errorCode,
  }).toString();

  return `${routesMap.loginPage}#${errorHash}`;
};

function* failSessionSaga() {
  yield call(G.removeCurrentBranchGuid);
  yield call(G.removeRefreshTokenFromSession);
  yield call(G.removeAuthTokenFromSession);

  yield put(A.receivedSessionValidationFail());

  yield call(G.goToRoute, routesMap.loginPage);

  yield put({ type: GC.CLEAR_STORE });
}

export function* sessionSaga(token) {
  try {
    if (R.includes(G.getWindowLocationPathname(), omitSessionPages)) return false;

    // TODO: move carrier portal logic to own saga
    const forCarrierData = G.parseQueryString();
    const tokenCarrier = forCarrierData.token;

    const portals = [
      GC.ROUTE_PATH_CARRIERS_ACCEPT,
      GC.ROUTE_PATH_CARRIERS_DECLINE,
      GC.ROUTE_PATH_ORDER_QUOTE_ACCEPT,
      GC.ROUTE_PATH_ORDER_QUOTE_DECLINE,
      GC.ROUTE_PATH_CARRIER_QUOTE_ACCEPT,
      GC.ROUTE_PATH_CARRIER_QUOTE_DECLINE,
    ];

    const isPortalScreen = R.any((item) => R.includes(item, G.getWindowLocationPathname()), portals);

    if (G.isTrue(isPortalScreen)) {
      const portalTypesMap = {
        [GC.ROUTE_PATH_CARRIERS_ACCEPT]: GC.PORTAL_TYPE_CARRIER,
        [GC.ROUTE_PATH_CARRIERS_DECLINE]: GC.PORTAL_TYPE_CARRIER,
        [GC.ROUTE_PATH_ORDER_QUOTE_ACCEPT]: GC.PORTAL_TYPE_CUSTOMER,
        [GC.ROUTE_PATH_ORDER_QUOTE_DECLINE]: GC.PORTAL_TYPE_CUSTOMER,
        [GC.ROUTE_PATH_CARRIER_QUOTE_ACCEPT]: GC.PORTAL_TYPE_CARRIER_QUOTE,
        [GC.ROUTE_PATH_CARRIER_QUOTE_DECLINE]: GC.PORTAL_TYPE_CARRIER_QUOTE,
      };

      const routePath = R.head(R.split('?', R.pathOr('', ['location', 'pathname'], window)));
      const portalType = G.getPropFromObject(routePath, portalTypesMap);

      const actionType = G.ifElse(
        R.includes('decline', routePath),
        GC.VISIT_PORTAL_DECLINE_PAGE,
        GC.VISIT_PORTAL_ACCEPT_PAGE,
      );

      return yield put({ type: actionType, payload: { portalType, token: tokenCarrier } });
    }

    const carrierDetailPage = yield call(
      G.stringContains,
      G.getWindowLocationPathname(),
      GC.ROUTE_PATH_CARRIERS_ADD_MESSAGE_PAGE,
    );

    if (G.isTrue(carrierDetailPage)) {
      if (R.is(String, tokenCarrier)) {
        yield put(getCarrierResponseTokenSuccess(tokenCarrier));

        const headers = { carrierToken: tokenCarrier };
        const options = { headers };

        const detailPageRes = yield call(
          sendRequest,
          'get',
          endpointsMap.getCarrierDetailPageEndpoint(tokenCarrier),
          options,
        );

        if (G.isResponseSuccess(detailPageRes.status)) {
          yield put(socketConnectRequest());
          yield put(getCarrierPortalInfoSuccess(detailPageRes.data));
          yield put(getChatMessageCarrierRequest(forCarrierData));

          const googleApiKey = R.pathOr(
            'AIzaSyB9Vj08tH1omYsW5k8GDWAY5PtfrE_OxAk',
            ['data', 'googleApiKey'],
            detailPageRes,
          );

          yield put(setGoogleApiKey(googleApiKey));

          yield delay(2000);

          yield put(socketPostMessage({
            action: messageType.subscribe,
            destination: `/tel/forCarrier/${tokenCarrier}`,
          }));
        }
      }

      return false;
    }

    const carrierResponseRoute = yield call(
      G.stringContains,
      G.getWindowLocationPathname(),
      GC.ROUTE_PATH_CARRIERS_RESPONSE,
    );

    if (G.isTrue(carrierResponseRoute)) return false;
    // finish carrier portal logic

    if (G.isNotNilAndNotEmpty(token)) {
      const res = yield call(sendRequest, 'post', endpointsMap.checkToken, { data: { token }, auth }, true);

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        if (R.isEmpty(res.data.authorities)) {
          yield call(G.showToastrMessage, 'info', 'messages:permissions:missing');

          yield call(G.goToRoute, routesMap.loginPage);
        } else {
          const userWithAccessToken = R.assoc('access_token', token, data);

          yield put(A.receivedSessionValidationSuccess(userWithAccessToken));
          yield put(setAuthoritiesRequest(data.authorities));
        }

        G.setItemToWindow('amousCurrentUser', data);
      } else {
        yield call(failSessionSaga);
      }
    } else {
      yield delay(1000);

      yield call(failSessionSaga);
    }
  } catch (error) {
    yield call(failSessionSaga);
  }
}


// Reset password
function* handleResetPasswordRequest({ payload }) {
  try {
    yield put(openLoader());

    const { email, loginId, callback } = payload;

    const reqData = { email, loginId };

    const res = yield call(sendRequest, 'post', endpointsMap.forgot, { data: reqData, auth }, true);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:forgot:psw');
      yield call(G.goToRoute, routesMap.loginPage);
    } else {
      yield call(G.handleFailResponse, res, 'handleResetPasswordRequest fail');
    }

    G.callFunction(callback);

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleResetPasswordRequest exception');
  }
}

function* handleCreateNewPasswordRequest({ payload }) {
  try {
    yield put(openLoader());

    const { newPassword, token, callback } = payload;

    const reqData = { newPassword };
    const headers = { Authorization: `Bearer ${token}` };

    const res = yield call(sendRequest, 'post', endpointsMap.createNewPassword, { data: reqData, headers });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:reset:psw');
      yield call(G.goToRoute, routesMap.loginPage);

      yield put(A.receivedCreateNewPasswordSuccess());
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateNewPasswordRequest fail');
    }

    G.callFunction(callback);

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleCreateNewPasswordRequest exception');
  }
}

export function* handleCreateNewPasswordMobileRequest({ payload }) {
  try {
    yield put(openLoader());

    const { token, newPassword, callback } = payload;

    const reqData = { newPassword };
    const headers = { Authorization: `Bearer ${token}` };

    const res = yield call(sendRequest, 'post', endpointsMap.createNewPassword, { data: reqData, headers });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.goToRoute, routesMap.passwordSuccessPage);

      yield put(A.receivedCreateNewPasswordMobileSuccess());
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateNewPasswordMobileRequest fail');
    }

    G.callFunction(callback);

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleCreateNewPasswordMobileRequest exception');
  }
}

// NAL
function* getUserSsoByNalIdSaga() {
  try {
    yield put(openLoader());

    const { nalId } = G.parseQueryString();

    if (G.isNilOrEmpty(nalId)) return yield call(G.goToRoute, routesMap.loginPage);

    const res = yield call(sendRequest, 'post', endpointsMap.userSsoByNalId, G.setParamToRequestQuery('nalId', nalId));

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const { authorities, access_token, refresh_token } = data;

      if (R.isEmpty(authorities)) {
        yield call(G.showToastrMessage, 'info', 'messages:permissions:missing');

        yield call(G.goToRoute, routesMap.loginPage);
      } else {
        const branchGuid = data[GC.FIELD_BRANCH_UNDERSCORE_GUID];

        yield call(G.setCurrentBranchGuid, branchGuid);
        yield call(G.setAuthTokenToSession, access_token);
        yield call(G.setRefreshTokenToSession, refresh_token);

        yield put(A.receivedNalLogInSuccess(data));
        yield put(setAuthoritiesRequest(authorities));
      }

      G.setItemToWindow('amousCurrentUser', data);
    } else {
      yield call(G.goToRoute, routesMap.loginPage);
      yield call(G.handleFailResponse, res, 'getUserSsoByNalIdSaga fail', true);
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.goToRoute, routesMap.loginPage);
    yield call(G.handleException, error, 'getUserSsoByNalIdSaga exception');
  }
}

function* handleVisitNALLoginPage() {
  while (true) { // eslint-disable-line
    yield call(getUserSsoByNalIdSaga);

    break;
  }
}

// BOSCH
function* handleVisitBoshRedirectPage() {
  try {
    yield put(openLoader());

    const res = yield call(sendRequest, 'get', endpointsMap.losSsoRedirectUrl);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(window.open, data, '_self');
    } else {
      yield call(G.goToRoute, routesMap.loginPage);
      yield call(G.handleFailResponse, res, 'handleVisitBoshRedirectPage fail', true);
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.goToRoute, routesMap.loginPage);
    yield call(G.handleException, error, 'handleVisitBoshRedirectPage exception');
  }
}

function* handleVisitAzureSsoRedirectPage() {
  try {
    yield put(openLoader());

    const redirectUrl = createAzureSsoRedirectUrl();

    yield call(window.open, redirectUrl, '_self');

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.goToRoute, routesMap.loginPage);
    yield call(G.handleException, error, 'handleVisitAzureSsoRedirectPage exception');
  }
}

function* handleVisitAzureSsoCallbackPage() {
  try {
    yield put(openLoader());

    const callbackData = G.parseHashString();
    const dnsPrefix = R.prop(GC.FIELD_BRANCH_DNS_PREFIX, callbackData);
    const callbackRedirectUrl = createAzureSsoCallbackRedirectUrl(dnsPrefix);

    if (G.isNotNilAndNotEmpty(callbackRedirectUrl)) {
      yield call(replaceWindowLocation, callbackRedirectUrl);

      return;
    }

    const errorCode = R.prop(GC.FIELD_ERROR, callbackData);

    if (G.isNotNilAndNotEmpty(errorCode)) {
      yield put(closeLoader());
      yield call(replaceWindowHistoryRoute, createLoginRouteWithError(errorCode));

      return;
    }

    const accessToken = R.prop('access_token', callbackData);
    const refreshToken = R.prop('refresh_token', callbackData);
    const rawExpiresIn = R.prop('expires_in', callbackData);
    const expiresIn = G.isNotNilAndNotEmpty(rawExpiresIn) ? rawExpiresIn : undefined;

    if (G.isNilOrEmpty(accessToken)) {
      yield put(closeLoader());
      yield call(G.goToRoute, routesMap.loginPage);

      return;
    }

    // We have tokens — strip them from the URL/history now, before the async check_token call and
    // before navigating, so the access/refresh tokens never linger in window.location.hash.
    clearWindowHash();

    const res = yield call(sendRequest, 'post', endpointsMap.checkToken, { data: { token: accessToken }, auth }, true);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      if (R.isEmpty(data.authorities)) {
        yield call(G.showToastrMessage, 'info', 'messages:permissions:missing');
        yield call(G.goToRoute, routesMap.loginPage);
      } else {
        const branchGuid = data[GC.FIELD_BRANCH_UNDERSCORE_GUID];

        const userWithAccessToken = R.mergeRight(data, {
          expires_in: expiresIn,
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        yield call(G.setCurrentBranchGuid, branchGuid);
        yield call(G.setAuthTokenToSession, accessToken);

        if (G.isNotNilAndNotEmpty(refreshToken)) {
          yield call(G.setRefreshTokenToSession, refreshToken);
        } else {
          yield call(G.removeRefreshTokenFromSession);
        }

        if (G.isNotNilAndNotEmpty(expiresIn)) {
          yield call(G.setAuthTokenExpirationToSession, expiresIn);
        }

        yield put(A.receivedLogInSuccess(userWithAccessToken));
        yield put(setAuthoritiesRequest(data.authorities));

        G.setItemToWindow('amousCurrentUser', userWithAccessToken);
      }
    } else {
      yield call(G.goToRoute, routesMap.loginPage);
      yield call(G.handleFailResponse, res, 'handleVisitAzureSsoCallbackPage fail', true);
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.goToRoute, routesMap.loginPage);
    yield call(G.handleException, error, 'handleVisitAzureSsoCallbackPage exception');
  }
}

function* handleVisitBoshCallbackPage() {
  try {
    yield put(openLoader());

    const { code, state } = G.parseQueryString();

    const res = yield call(sendRequest, 'get', endpointsMap.losSsoToken, { params: { code, state } });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const { authorities, access_token, refresh_token } = data;

      if (R.isEmpty(authorities)) {
        yield call(G.showToastrMessage, 'info', 'messages:permissions:missing');

        yield call(G.goToRoute, routesMap.loginPage);
      } else {
        const branchGuid = data[GC.FIELD_BRANCH_UNDERSCORE_GUID];

        yield call(G.setCurrentBranchGuid, branchGuid);
        yield call(G.setAuthTokenToSession, access_token);
        yield call(G.setRefreshTokenToSession, refresh_token);

        yield put(A.receivedBoshLogInSuccess(data));
        yield put(setAuthoritiesRequest(authorities));
      }

      G.setItemToWindow('amousCurrentUser', data);
    } else {
      yield call(G.goToRoute, routesMap.loginPage);
      yield call(G.handleFailResponse, res, 'handleVisitBoshCallbackPage fail', true);
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.goToRoute, routesMap.loginPage);
    yield call(G.handleException, error, 'handleVisitBoshCallbackPage exception');
  }
}

function* getTokenByCurrentBranchGuidRequest() {
  try {
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: {
        [GC.CURRENT_BRANCH_GUID]: currentBranchGuid,
      },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.token, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.setAuthTokenToSession, data.access_token);
      yield call(G.setRefreshTokenToSession, data.refresh_token);
      yield call(G.setAuthTokenExpirationToSession, data.expires_in);
    } else {
      yield call(G.handleException, 'error', 'getTokenByCurrentBranchGuidRequest exception');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getTokenByCurrentBranchGuidRequest exception');
  }
}

function* loginWatcherSaga() {
  yield takeEvery('*', handleDispatchAnyAction);
  yield fork(checkTokenAndRefreshTokenSetDelaySaga);
  yield takeLatest(A.sendLogInRequest, handleLoginRequest);
  yield takeLatest(sendLogOutRequest, logoutWithReloadSaga);
  yield takeLatest(A.sendLogOutRequest, logoutWithReloadSaga);
  yield takeLatest(A.checkTokenRequest, handleCheckTokenSaga);
  yield takeLatest(A.refreshTokenRequest, handleRefreshTokenSaga);
  yield takeLatest(A.sendResetPasswordRequest, handleResetPasswordRequest);
  yield takeLatest(A.sendCreateNewPasswordRequest, handleCreateNewPasswordRequest);
  yield takeLatest(receivedSwitchBranchSuccess, getTokenByCurrentBranchGuidRequest);
  yield takeLatest(A.getTokenByCurrentBranchGuidRequest, getTokenByCurrentBranchGuidRequest);
  yield takeLatest(A.sendCreateNewPasswordMobileRequest, handleCreateNewPasswordMobileRequest);
  // NAL
  yield takeLatest(GC.VISIT_NAL_LOGIN_PAGE, handleVisitNALLoginPage);
  // Azure SSO
  yield takeLatest(GC.VISIT_AZURE_SSO_REDIRECT_PAGE, handleVisitAzureSsoRedirectPage);
  yield takeLatest(GC.VISIT_AZURE_SSO_CALLBACK_PAGE, handleVisitAzureSsoCallbackPage);
  // BOSCH
  yield takeLatest(GC.VISIT_BOSH_REDIRECT_PAGE, handleVisitBoshRedirectPage);
  yield takeLatest(GC.VISIT_BOSH_CALLBACK_PAGE, handleVisitBoshCallbackPage);
}

export default loginWatcherSaga;
